// Steuerpaket S3: lädt Buchungen, Klärungen und (bei WEG) Schlüssel + Einheiten eines Objekts und rechnet je Jahr.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ladeBuchungen, ladeImportStand, ladeKlaerungen, ladeSchluessel, ladeWegEinheiten, type ImportStand, type KlaerungZeile } from './api';
import { berechneObjekt, type Buchung, type ObjektErgebnis } from './steuerpaket';
import { verteileWeg, type Kostenschluessel, type WegEinheit, type WegVerteilung } from './weg';

export interface SteuerDaten {
  laedt: boolean;
  fehler: string | null;
  stand: ImportStand | null;
  buchungen: Buchung[];
  klaerungen: KlaerungZeile[];
  schluessel: Kostenschluessel[];
  einheiten: WegEinheit[];
  /** Jahre mit Zahlungen, neueste zuerst */
  jahre: number[];
  neuLaden: () => void;
}

export function useSteuerDaten(objektId: string | null, weg: boolean): SteuerDaten {
  const [z, setZ] = useState<Omit<SteuerDaten, 'jahre' | 'neuLaden'>>({ laedt: true, fehler: null, stand: null, buchungen: [], klaerungen: [], schluessel: [], einheiten: [] });
  const [zaehler, setZaehler] = useState(0);
  useEffect(() => {
    if (!objektId) return;
    let aktiv = true;
    setZ((v) => ({ ...v, laedt: true, fehler: null }));
    void (async () => {
      try {
        const [stand, buchungen, klaerungen, schluessel, einheiten] = await Promise.all([
          ladeImportStand(objektId), ladeBuchungen(objektId), ladeKlaerungen(objektId),
          weg ? ladeSchluessel(objektId) : Promise.resolve([]), weg ? ladeWegEinheiten(objektId) : Promise.resolve([]),
        ]);
        if (aktiv) setZ({ laedt: false, fehler: null, stand, buchungen, klaerungen, schluessel, einheiten });
      } catch (e) {
        if (aktiv) setZ((v) => ({ ...v, laedt: false, fehler: (e as Error).message }));
      }
    })();
    return () => { aktiv = false; };
  }, [objektId, weg, zaehler]);
  const jahre = useMemo(() => [...new Set(z.buchungen.map((b) => Number((b.wertstellung ?? '').slice(0, 4))).filter((j) => j > 2000))].sort((a, b) => b - a), [z.buchungen]);
  const neuLaden = useCallback(() => setZaehler((n) => n + 1), []);
  return { ...z, jahre, neuLaden };
}

/** Standardjahr: das letzte abgeschlossene Jahr, sofern Daten da sind – sonst das neueste. */
export function standardJahr(jahre: readonly number[], heute = new Date()): number | null {
  const vorjahr = heute.getFullYear() - 1;
  return jahre.includes(vorjahr) ? vorjahr : (jahre[0] ?? null);
}

export function useAufstellung(d: SteuerDaten, jahr: number | null): { ergebnis: ObjektErgebnis | null; verteilung: WegVerteilung | null } {
  return useMemo(() => {
    if (jahr == null || d.buchungen.length === 0) return { ergebnis: null, verteilung: null };
    const ergebnis = berechneObjekt(d.buchungen, jahr, d.klaerungen);
    return { ergebnis, verteilung: d.einheiten.length ? verteileWeg(ergebnis, d.einheiten, d.schluessel) : null };
  }, [d.buchungen, d.klaerungen, d.schluessel, d.einheiten, jahr]);
}
